import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firebase';
import { collection, getDocs } from 'firebase/firestore';
import StatCard from '../components/shared/StatCard';
import '../styles/Dashboard.css';

const VENDOR_PALETTE = ['#3b82f6', '#00bf63', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#0ea5e9'];

const initialsFor = (name) => {
  if (!name) return '—';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    companies: 0,
    employees: 0,
    vendors: 0,
    offers: 0,
    activeVendors: 0,
  });
  const [topCompanies, setTopCompanies] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [companiesSnap, usersSnap, vendorsSnap, offersSnap] = await Promise.all([
          getDocs(collection(db, 'companies')),
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'vendors')),
          getDocs(collection(db, 'offers')),
        ]);

        const companies = companiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const vendors = vendorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const offers = offersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const employeeCountByCompany = {};
        usersSnap.docs.forEach(d => {
          const cid = d.data().companyId;
          if (!cid) return;
          employeeCountByCompany[cid] = (employeeCountByCompany[cid] || 0) + 1;
        });

        const offerCountByVendor = {};
        offers.forEach(o => {
          const vid = o.vendorId;
          if (!vid) return;
          offerCountByVendor[vid] = (offerCountByVendor[vid] || 0) + 1;
        });

        const companiesEnriched = companies
          .map(c => ({
            ...c,
            employeeCount: employeeCountByCompany[c.id] ?? c.employeeCount ?? 0,
          }))
          .sort((a, b) => (b.employeeCount || 0) - (a.employeeCount || 0));

        const vendorsEnriched = vendors
          .map(v => ({
            ...v,
            offerCount: offerCountByVendor[v.id] || 0,
          }))
          .sort((a, b) => (b.offerCount || 0) - (a.offerCount || 0));

        const activeVendors = vendors.filter(v => v.isActive !== false).length;

        if (cancelled) return;
        setStats({
          companies: companies.length,
          employees: usersSnap.size,
          vendors: vendors.length,
          offers: offers.length,
          activeVendors,
        });
        setTopCompanies(companiesEnriched.slice(0, 5));
        setTopVendors(vendorsEnriched.slice(0, 4));
        setLoading(false);
      } catch (err) {
        console.error('Dashboard load failed:', err);
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const planFor = (c) => {
    return c.plan || (c.employeeCount > 100 ? 'Premium' : c.employeeCount > 30 ? 'Enhanced' : 'Basic');
  };

  const planStatusClass = (plan) => {
    if (plan === 'Premium') return 'premium';
    if (plan === 'Enhanced') return 'active';
    return 'basic';
  };

  const healthPct = stats.vendors === 0 ? 100 : Math.round((stats.activeVendors / stats.vendors) * 1000) / 10;
  const activeCompanies = topCompanies.filter(c => (c.employeeCount || 0) > 0).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Stat cards */}
      <div className="loysta-stat-grid">
        <StatCard
          icon="building"
          label="Companies"
          value={loading ? '—' : stats.companies}
          sub={loading ? ' ' : `${activeCompanies} active`}
          variant="blue"
        />
        <StatCard
          icon="store"
          label="Vendors"
          value={loading ? '—' : stats.vendors}
          sub={loading ? ' ' : `${stats.activeVendors} active`}
          variant="purple"
        />
        <StatCard
          icon="users"
          label="Total Employees"
          value={loading ? '—' : stats.employees.toLocaleString()}
          sub="Across all companies"
          variant="green"
        />
        <StatCard
          icon="tag"
          label="Total Offers"
          value={loading ? '—' : stats.offers.toLocaleString()}
          sub="Across all vendors"
          dark
        />
      </div>

      {/* Main grid: top companies table + right sidebar */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Top Companies by Engagement */}
        <div className="loysta-card" style={{ flex: 1, minWidth: 0 }}>
          <div className="loysta-card-head">
            <span className="loysta-card-title">Top Companies by Engagement</span>
          </div>
          {loading ? (
            <div style={{ padding: 32, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
          ) : topCompanies.length === 0 ? (
            <div style={{ padding: 32, color: '#94a3b8', fontSize: 13 }}>No companies yet.</div>
          ) : (
            <table className="loysta-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Employees</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {topCompanies.map((c, i) => {
                  const color = VENDOR_PALETTE[i % VENDOR_PALETTE.length];
                  const plan = planFor(c);
                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {c.logoUrl ? (
                            <img
                              src={c.logoUrl}
                              alt={c.name}
                              style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <div style={{
                              width: 30, height: 30, borderRadius: 8,
                              background: color + '20', color, flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700,
                            }}>{initialsFor(c.name)}</div>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{c.name || 'Unnamed'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`loysta-status ${planStatusClass(plan)}`}>{plan}</span>
                      </td>
                      <td style={{ color: '#334155' }}>{c.employeeCount || 0}</td>
                      <td>
                        <span className={`loysta-status ${(c.employeeCount || 0) > 0 ? 'active' : 'inactive'}`}>
                          {(c.employeeCount || 0) > 0 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Platform Health */}
          <div style={{
            background: '#0f172a',
            borderRadius: 12,
            padding: 20,
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: -20, right: -20,
              width: 100, height: 100, borderRadius: '50%',
              background: 'rgba(193,255,114,0.07)', pointerEvents: 'none',
            }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 4 }}>Platform Health</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#c1ff72', marginBottom: 4 }}>
              {loading ? '—' : `${healthPct}%`}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              {stats.activeVendors} of {stats.vendors} vendors active
            </div>
            <div style={{ marginTop: 14, height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${loading ? 0 : healthPct}%`,
                background: '#c1ff72',
                borderRadius: 2,
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>

          {/* Top Vendors */}
          <div className="loysta-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Top Vendors</div>
            {loading ? (
              <div style={{ color: '#94a3b8', fontSize: 12 }}>Loading…</div>
            ) : topVendors.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 12 }}>No vendors yet.</div>
            ) : (
              topVendors.map((v, i) => {
                const name = v.businessName || v.name || v.email || 'Vendor';
                const isActive = v.isActive !== false;
                return (
                  <div key={v.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: i < topVendors.length - 1 ? 12 : 0,
                  }}>
                    {v.logoUrl ? (
                      <img
                        src={v.logoUrl}
                        alt={name}
                        style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{
                        width: 28, height: 28, borderRadius: 7,
                        background: 'rgba(0,191,99,0.10)', color: '#00bf63',
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700,
                      }}>{initialsFor(name)}</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, fontWeight: 600, color: '#0f172a',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {v.offerCount} {v.offerCount === 1 ? 'offer' : 'offers'}
                      </div>
                    </div>
                    <span className={`loysta-status ${isActive ? 'active' : 'inactive'}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
